/**
 * Cart Business Rules (Layer 0)
 * 
 * 职责：核心业务规则判定（纯函数）
 * - 库存检查
 * - 数量限制
 * - 免邮判定
 */

export const MAX_STOCK_LIMIT = 999;

/**
 * 库存检查结果
 */
export interface StockCheckResult {
    /** 是否检查通过 */
    ok: boolean;
    /** 错误明细 */
    error?: {
        /** 错误码：库存超限 */
        code: 'STOCK_LIMIT';
        /** 最大可用库存 */
        maxQty: number;
        /** 请求的数量 */
        requestedQty: number;
    };
}


/**
 * 检查请求数量是否超过最大可用库存
 * @param requestedQty - 想要添加/更新的数量
 * @param maxAvailable - 最大可用库存 (默认为 999)
 * @returns 校验结果对象
 */
export function checkStockLimit(requestedQty: number, maxAvailable: number = MAX_STOCK_LIMIT): StockCheckResult {
    if (requestedQty > maxAvailable) {
        return {
            ok: false,
            error: {
                code: 'STOCK_LIMIT',
                maxQty: maxAvailable,
                requestedQty,
            },
        };
    }
    return { ok: true };
}


/**
 * 限制数量在有效范围内 [1, max]
 * @param qty - 原始数量
 * @param max - 最大允许数量 (默认为 999)
 * @returns 限制后的有效数量
 */
export function clampQuantity(qty: number, max: number = MAX_STOCK_LIMIT): number {
    return Math.max(1, Math.min(qty, max));
}


/**
 * 判定当前金额是否已达到免邮条件
 * @param totalCents - 当前金额 (分)
 * @param thresholdCents - 免邮阈值 (分)
 * @returns 是否免邮
 */
export function isFreeShipping(totalCents: number, thresholdCents: number): boolean {
    return thresholdCents > 0 && totalCents >= thresholdCents;
}


/**
 * 检查是否达到最低订单金额
 * @param totalCents - 当前金额 (分)
 * @param minOrderCents - 最低订单限制 (分)
 * @returns 校验结果及剩余还需多少金额
 */
export function checkMinOrder(totalCents: number, minOrderCents: number): { ok: boolean; remaining: number } {
    if (totalCents > 0 && totalCents < minOrderCents) {
        return { ok: false, remaining: minOrderCents - totalCents };
    }
    return { ok: true, remaining: 0 };
}


/**
 * 检查当前金额是否支持货到付款 (COD)
 * @param totalCents - 当前金额 (分)
 * @param codThresholdCents - COD 最低金额要求 (分)
 * @returns 校验结果及剩余还需多少金额
 */
export function checkCodAvailability(totalCents: number, codThresholdCents: number): { ok: boolean; remaining: number } {
    if (totalCents < codThresholdCents) {
        return { ok: false, remaining: codThresholdCents - totalCents };
    }
    return { ok: true, remaining: 0 };
}


/**
 * 解析 Shopify 返回的库存错误消息，从中提取最大可用库存数
 * @param errorMessage - 原始错误消息文本
 * @returns 包含提取结果的对象
 */
export function parseStockError(errorMessage: string): { isStockError: boolean; maxQty?: number } {
    // 检测库存超限特征（支持多语言）
    const isStockLimitError = /solo \d+ articoli|only \d+ items|more than \d+|disponibilità|availability|stock|inventario/i.test(errorMessage);

    if (!isStockLimitError) {
        return { isStockError: false };
    }

    // 从错误消息中提取库存数量
    const match = errorMessage.match(/(?:solo|only)\s+(\d+)\s+(?:articoli|items)|more than\s+(\d+)/i);
    const maxQty = match ? parseInt(match[1] || match[2], 10) : undefined;

    return { isStockError: true, maxQty };
}


/**
 * 检测 Shopify 静默库存修正（API 返回 200，但实际修改后的数量小于请求期望值）
 * @param requestedQty - 用户请求的期望数量
 * @param actualQty - 接口实际返回的数量
 * @returns 是否发生了库存修正
 */
export function detectStockCorrection(requestedQty: number, actualQty: number): boolean {
    return actualQty < requestedQty;
}
